import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Check,
  CheckCircle2,
  CircleDot,
  Clock3,
  FileText,
  Loader2,
  X,
  XCircle,
} from "lucide-react";
import axios from "../../../../axios";
import { toast } from "react-toastify";
import { useUser } from "../../../../hooks/use-user";

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getPayload = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  if (result?.attempt && typeof result.attempt === "object") {
    return result.attempt;
  }

  return result || data || {};
};

const getRows = (payload) => {
  const candidates = [
    payload?.answers,
    payload?.questions,
    payload?.answerKey,
    payload?.responses,
    payload?.attempt?.answers,
    payload?.attempt?.questions,
    payload?.result?.answers,
    payload?.result?.questions,
    payload?.data?.answers,
    payload?.data?.questions,
  ];

  return candidates.find((item) => Array.isArray(item)) || [];
};

const normalizeText = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const normalizeForCompare = (value) => normalizeText(value).toLowerCase();

const formatTime = (seconds) => {
  const value = Number(seconds || 0);
  if (!Number.isFinite(value) || value <= 0) return "0s";

  const mins = Math.floor(value / 60);
  const secs = Math.floor(value % 60);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const getQuestionSource = (item) => {
  if (item?.question && typeof item.question === "string") return item;

  return (
    item?.question ||
    item?.mockQuestion ||
    item?.mockTestQuestion ||
    item?.questionData ||
    item
  );
};

const getOptions = (source) => {
  if (Array.isArray(source?.options)) return source.options;
  if (Array.isArray(source?.optionsEng)) return source.optionsEng;

  return [
    source?.optionA,
    source?.optionB,
    source?.optionC,
    source?.optionD,
  ].filter((option) => option !== undefined && option !== null);
};

const resolveQuestion = (item, index) => {
  const source = getQuestionSource(item);
  const question = pickFirst(
    item?.questionText,
    source?.question,
    source?.questionEng,
    source?.englishQuestion,
    `Question ${index + 1}`,
  );
  const correctAnswer = pickFirst(
    item?.correctAnswer,
    item?.rightAnswer,
    item?.rightAnsEng,
    source?.answer,
    source?.correctAnswer,
    "",
  );
  const userAnswer = pickFirst(
    item?.userAnswer,
    item?.selectedAnswer,
    item?.submittedAnswer,
    item?.answer,
    "",
  );
  const type = normalizeText(item?.type || item?.status).toLowerCase();
  const isSkipped =
    item?.isSkipped === true ||
    item?.skipped === true ||
    type === "skip" ||
    type === "skipped" ||
    !normalizeText(userAnswer);
  const isCorrect =
    !isSkipped &&
    (item?.ansRight === true ||
      item?.isCorrect === true ||
      normalizeForCompare(userAnswer) === normalizeForCompare(correctAnswer));

  return {
    id: item?._id || source?._id || index,
    number: item?.questionNo || item?.number || index + 1,
    question: normalizeText(question),
    options: getOptions(source),
    correctAnswer: normalizeText(correctAnswer),
    userAnswer: normalizeText(userAnswer),
    timeTaken: item?.timeTaken || item?.secondsTaken || item?.duration || 0,
    isSkipped,
    isCorrect,
  };
};

const AnswerBadge = ({ question }) => {
  if (question.isSkipped) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-[#F4ECFF] px-4 py-2 text-sm font-semibold text-[#6B2B86]">
        <CircleDot size={16} />
        Skipped
      </span>
    );
  }

  if (question.isCorrect) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-[#E7F8EE] px-4 py-2 text-sm font-semibold text-[#15935E]">
        <CheckCircle2 size={16} />
        Correct
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-[#FDE9EC] px-4 py-2 text-sm font-semibold text-[#C83E4D]">
      <XCircle size={16} />
      Wrong
    </span>
  );
};

const MockTestResult = () => {
  const { mockTestId } = useParams();
  const { data: user } = useUser();
  const [payload, setPayload] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const fetchResult = async () => {
      setLoading(true);

      try {
        const params = {
          ...(user?._id ? { userId: user._id } : {}),
          mockTest: mockTestId,
        };
        const response = await axios.get(`/mock-tests/${mockTestId}/my-result`, {
          params,
        });

        setPayload(getPayload(response));
      } catch (error) {
        toast.error("Unable to load result");
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [mockTestId, user?._id]);

  useEffect(() => {
    const fetchAnalysis = async () => {
      if (activeTab !== "subjects" || analysis) return;

      setAnalysisLoading(true);

      try {
        const response = await axios.get(`/mock-tests/${mockTestId}/my-analysis`, {
          params: user?._id ? { userId: user._id } : {},
        });
        setAnalysis(response?.data?.result || response?.data?.data || response?.data || {});
      } catch (error) {
        toast.error("Unable to load analysis");
      } finally {
        setAnalysisLoading(false);
      }
    };

    fetchAnalysis();
  }, [activeTab, analysis, mockTestId, user?._id]);

  const result = useMemo(() => {
    const rows = getRows(payload || {});
    const correct = Number(
      pickFirst(
        payload?.summary?.correct,
        payload?.correct,
        payload?.summary?.rightCount,
        payload?.rightCount,
        payload?.rightAnswers,
        payload?.correctAnswers,
        payload?.correctCount,
        rows.filter((row) => row?.ansRight === true || row?.isCorrect === true).length,
        0,
      ),
    );
    const wrong = Number(
      pickFirst(
        payload?.summary?.wrong,
        payload?.wrong,
        payload?.summary?.wrongCount,
        payload?.wrongCount,
        payload?.wrongAnswers,
        payload?.incorrectAnswers,
        payload?.incorrectCount,
        rows.filter((row) => row?.ansRight === false || row?.isCorrect === false).length,
        0,
      ),
    );
    const skipped = Number(
      pickFirst(
        payload?.summary?.skipped,
        payload?.skipped,
        payload?.summary?.skipCount,
        payload?.skipCount,
        payload?.skippedAnswers,
        0,
      ),
    );
    const total = Number(
      pickFirst(
        payload?.summary?.totalQuestions,
        payload?.totalQuestions,
        payload?.mockTest?.totalQuestions,
        payload?.total,
        rows.length,
        0,
      ),
    );
    const score = Number(
      pickFirst(payload?.score, payload?.marks, payload?.totalScore, correct, 0),
    );
    const timeTaken = pickFirst(
      payload?.summary?.timeTakenSeconds,
      payload?.timeTakenSeconds,
      payload?.timeTaken,
      payload?.totalTimeTaken,
      0,
    );
    const accuracy =
      total > 0
        ? Math.round((correct / total) * 100)
        : Number(pickFirst(payload?.accuracy, payload?.percentage, 0));

    return {
      title:
        pickFirst(
          payload?.mockTest?.title,
          payload?.mockTest?.name,
          payload?.title,
          payload?.name,
          payload?.mockTestName,
          "Mock Test Report",
        ),
      score,
      total,
      correct,
      wrong,
      skipped,
      timeTaken,
      accuracy,
      rank: pickFirst(payload?.rank, payload?.userRank, 0),
      percentile: pickFirst(payload?.percentage, 0),
      rows: rows.map(resolveQuestion),
    };
  }, [payload]);

  const analysisRows = useMemo(() => {
    const candidates = [
      analysis?.subjectProgress,
      analysis?.subjects,
      analysis?.subjectAnalysis,
      analysis?.sections,
      analysis?.analysis,
      Array.isArray(analysis) ? analysis : null,
    ];

    return candidates.find((item) => Array.isArray(item)) || [];
  }, [analysis]);

  return (
    <div className="min-h-screen bg-[#fbfafc] pb-24 text-[#211629]">
      <main className="mx-auto max-w-6xl px-4 pt-8 pb-5 sm:px-6 lg:px-8 lg:pt-14">
        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center rounded-3xl bg-white shadow-sm">
            <Loader2 className="h-8 w-8 animate-spin text-[#562566]" />
          </div>
        ) : (
          <>
            <section className="rounded-[1.5rem] bg-[#562566] p-4 text-left text-white shadow-[0_14px_28px_rgba(86,37,102,0.16)] sm:p-5 lg:p-7">
              <p className="text-xs font-bold text-white/75">Report Ready</p>
              <h1 className="mt-1 text-2xl font-extrabold leading-tight">
                Mock Test Report
              </h1>
              <p className="mt-1 text-sm font-medium text-white/80">
                Performance Based Analysis
              </p>

              <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div className="rounded-xl bg-white px-4 py-3 text-[#211629]">
                  <p className="text-xs font-bold text-[#8a8490]">Score</p>
                  <p className="mt-1 text-lg font-extrabold text-[#562566]">
                    {result.score}/{result.total}
                  </p>
                </div>
                <div className="rounded-xl bg-white px-4 py-3 text-[#211629]">
                  <p className="text-xs font-bold text-[#8a8490]">Accuracy</p>
                  <p className="mt-1 text-lg font-extrabold text-green-600">
                    {result.accuracy}%
                  </p>
                </div>
                <div className="rounded-xl bg-white px-4 py-3 text-[#211629]">
                  <p className="text-xs font-bold text-[#8a8490]">Rank</p>
                  <p className="mt-1 text-lg font-extrabold text-[#F5A623]">
                    #{result.rank}
                  </p>
                </div>
                <div className="rounded-xl bg-white px-4 py-3 text-[#211629]">
                  <p className="text-xs font-bold text-[#8a8490]">Percentile</p>
                  <p className="mt-1 text-lg font-extrabold text-[#562566]">
                    {result.percentile}%
                  </p>
                </div>
              </div>
            </section>

            <div className="mt-5 flex gap-3 overflow-x-auto pb-1">
              {[
                ["overview", "Overview"],
                ["subjects", "Subject Analysis"],
                ["answers", "Answer Key"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className={`h-10 shrink-0 rounded-full px-5 text-xs font-extrabold ${
                    activeTab === key
                      ? "bg-[#562566] text-white"
                      : "bg-[#f7f2f8] text-[#6e6875]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeTab === "overview" && (
              <section className="mt-5 rounded-2xl border border-[#eee8f0] bg-white p-4 text-left shadow-sm lg:p-6">
                <h2 className="text-base font-extrabold text-[#211629]">
                  Summary
                </h2>
                <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div>
                    <p className="text-xs font-bold text-[#8a8490]">Correct</p>
                    <p className="mt-1 text-lg font-extrabold text-green-600">
                      {result.correct}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#8a8490]">Incorrect</p>
                    <p className="mt-1 text-lg font-extrabold text-red-500">
                      {result.wrong}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#8a8490]">Skipped</p>
                    <p className="mt-1 text-lg font-extrabold text-[#F5A623]">
                      {result.skipped}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#8a8490]">Time Taken</p>
                    <p className="mt-1 text-lg font-extrabold text-[#562566]">
                      {result.timeTaken}s
                    </p>
                  </div>
                </div>
              </section>
            )}

            {activeTab === "subjects" && (
              <section className="mt-5 rounded-2xl border border-[#eee8f0] bg-white p-4 text-left shadow-sm lg:p-6">
                <h2 className="text-base font-extrabold text-[#211629]">
                  Subject Analysis
                </h2>
                {analysisLoading ? (
                  <div className="mt-4 flex min-h-[120px] items-center justify-center rounded-xl bg-[#f7f2f8]">
                    <Loader2 className="h-6 w-6 animate-spin text-[#562566]" />
                  </div>
                ) : analysisRows.length ? (
                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    {analysisRows.map((item, index) => {
                      const total = Number(
                        pickFirst(item?.totalQuestions, item?.total, item?.questions, 0),
                      );
                      const correct = Number(
                        pickFirst(item?.correct, item?.right, item?.correctAnswers, 0),
                      );
                      const accuracy = pickFirst(
                        item?.accuracy,
                        item?.percentage,
                        total > 0 ? Math.round((correct / total) * 100) : 0,
                      );
                      const progress = pickFirst(item?.progress, 0);

                      return (
                        <div
                          key={item?._id || item?.subject || index}
                          className="rounded-xl bg-[#f7f2f8] p-4"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <BookIcon />
                              <div>
                                <p className="font-extrabold text-[#211629]">
                                  {pickFirst(item?.subject, item?.name, item?.title, "Subject")}
                                </p>
                                <p className="text-sm text-[#6e6875]">
                                  {correct} correct out of {total} • {progress}% progress
                                </p>
                              </div>
                            </div>
                            <span className="text-sm font-extrabold text-[#562566]">
                              {accuracy}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-4 rounded-xl bg-[#f7f2f8] p-4 text-sm font-semibold text-[#6e6875]">
                    Subject analysis is not available yet.
                  </div>
                )}
              </section>
            )}

            {activeTab === "answers" && (
              <section className="mt-5 grid gap-5 lg:grid-cols-2">
                {result.rows.map((question, index) => (
                  <div
                    key={question.id || index}
                    className="rounded-3xl bg-white p-5 text-left shadow-[0_6px_18px_rgba(54,33,69,0.14)]"
                  >
                    <div className="mb-3 flex items-start justify-between gap-4">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F4ECFF] text-sm font-bold text-[#562566]">
                        Q{question.number}
                      </span>
                      <AnswerBadge question={question} />
                    </div>

                    <h2 className="text-left text-lg font-semibold leading-7 text-[#1F1B24]">
                      {question.question || `Question ${index + 1}`}
                    </h2>

                    <div className="mt-4 flex flex-wrap gap-3">
                      <span className="inline-flex items-center gap-2 rounded-full bg-[#F8F3FA] px-4 py-2 text-sm font-semibold text-[#6C6172]">
                        <Clock3 size={17} />
                        {formatTime(question.timeTaken || question?.timeTakenSeconds || 0)}
                      </span>
                      <span className="inline-flex items-center gap-2 rounded-full bg-[#F8F3FA] px-4 py-2 text-sm font-semibold text-[#6C6172]">
                        <CircleDot size={17} />
                        {question.isSkipped ? "Skipped" : "Answered"}
                      </span>
                    </div>

                    <div className="mt-4 space-y-2">
                      {question.options.map((option, optionIndex) => {
                        const optionText = normalizeText(option);
                        const isCorrect =
                          normalizeForCompare(optionText) ===
                          normalizeForCompare(question.correctAnswer);
                        const isUserAnswer =
                          normalizeForCompare(optionText) ===
                          normalizeForCompare(question.userAnswer);
                        const isWrongUserAnswer =
                          isUserAnswer && !isCorrect && !question.isSkipped;
                        const optionLetter = String.fromCharCode(65 + optionIndex);

                        return (
                          <div
                            key={`${question.id}-${optionLetter}`}
                            className={`flex min-h-[60px] items-center gap-3 rounded-2xl border px-3 py-2 text-left ${
                              isCorrect
                                ? "border-[#7DDDB1] bg-[#E9F8EF]"
                                : isWrongUserAnswer
                                  ? "border-[#EF9EAA] bg-[#FDE9EC]"
                                  : "border-[#DDD6E3] bg-[#FBF8FD]"
                            }`}
                          >
                            <span
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-sm font-bold ${
                                isCorrect
                                  ? "border-[#22B978] bg-[#22B978] text-white"
                                  : isWrongUserAnswer
                                    ? "border-[#E0475F] bg-[#E0475F] text-white"
                                    : "border-[#CFC7D8] bg-white text-[#7B7282]"
                              }`}
                            >
                              {isCorrect ? (
                                <Check size={20} />
                              ) : isWrongUserAnswer ? (
                                <X size={20} />
                              ) : (
                                optionLetter
                              )}
                            </span>

                            <span className="min-w-0 flex-1 text-base font-medium">
                              {optionText}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
};

const BookIcon = () => (
  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#562566]">
    <FileText className="h-5 w-5" />
  </div>
);

export default MockTestResult;
